import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { NgPipesModule } from 'ngx-pipes';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';

import { fuseAnimations } from '../../../../@fuse/animations';
import { FuseDirectivesModule } from '../../../../@fuse/directives/directives';
import { FusePipesModule } from '../../../../@fuse/pipes/pipes.module';
import { FuseTranslationLoaderService } from '../../../../@fuse/services/translation-loader.service';
import { MaterialCategoryService } from '../../api/material-category.service';
import { MaterialsService } from '../../api/materials.service';
import { GT2PageAbstract } from '../../content/abstract/GT2PageAbstract';
import { Gt2PrimengModule } from '../../modules/gt2-primeng.module';
import {
  EditMaterialComboPanelData,
  EditMaterialComboPanelService,
} from '../../services/edit-material-combo-panel.service';
import { MobileLayoutService } from '../../services/mobile-layout.service';
import { ItemUrlFinder } from '../../utils/item-url-finder';
import { CreateMaterialComponent } from '../dialogs/create-material/create-material.component';
import { TagsService } from '../tags/tags.service';
import { locale as english } from './i18n/en-CA';
import { locale as french } from './i18n/fr-CA';

const SPINNER = 'materialComboSpinner';

/**
 * Panneau latéral « Modifier l'ensemble » (combo Matériel).
 *
 * Gauche : matériel catalogue non-ensemble (recherche + catégorie + tags).
 * Droite : contenu de l'ensemble — réordonnancement, quantité, flags par ligne
 * (affichage interne / prix unitaire), retrait.
 *
 * Persistance : attach / detach / move / flags / +/- sont envoyés EN DIRECT ;
 * « Enregistrer » pousse les quantités saisies (POST combo/update). L'appelant
 * recharge dans tous les cas de fermeture.
 *
 * Contexte commande (`orderContext`) : les enfants sont des CLONES commandés —
 * leur uuid diffère du catalogue, donc jamais de dédoublonnage par uuid entre
 * les deux listes ; après chaque attach on remplace `combo` par les enfants
 * renvoyés par l'API pour connaître l'uuid du nouveau clone.
 */
@Component({
  selector: 'app-edit-material-combo-panel',
  templateUrl: './edit-material-combo-panel.component.html',
  styleUrls: ['./edit-material-combo-panel.component.scss'],
  animations: fuseAnimations,
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FuseDirectivesModule,
    FusePipesModule,
    Gt2PrimengModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTooltipModule,
    DragDropModule,
    MatInputModule,
    MatIconModule,
    TranslateModule,
    MatProgressSpinnerModule,
    NgPipesModule,
    NgxSpinnerModule,
  ],
})
export class EditMaterialComboPanelComponent
  extends GT2PageAbstract
  implements OnInit, AfterViewInit, OnDestroy
{
  material: any;
  materials: any[] = [];
  combo: any[] = [];
  comboCopy: any[] = [];
  creationMode: boolean = false;
  orderContext: boolean = false;

  isLoading: boolean = false;
  isEmpty: boolean = true;
  saving: boolean = false;
  /** Une opération enfant (attach / detach / move / flag) est en vol. */
  childProcess: boolean = false;

  @ViewChild('filter') filter?: ElementRef;
  @ViewChild('materialList') materialList: ElementRef | any;
  filterValue: string = '';
  fromEventSubscription?: Subscription;
  newDialogRef: any;
  isSmallScreen: boolean = false;
  readonly mobileLayout = inject(MobileLayoutService);

  // Filtres additionnels (catégorie de matériel + tags) — mêmes sources que le
  // panneau « Ajouter un matériel ».
  materialCategories: any[] = [];
  selectedCategoryUuid: string | null = null;
  availableTags: any[] = [];
  selectedTagUuids: string[] = [];

  /** Affichage uniquement : seuil pour « aucun résultat » (l'API reste à 2 car. via le debounce). */
  readonly minCharsForEmptyMessage = 3;

  // Réordonnancement optimiste : on déplace localement, on appelle move-before /
  // move-after, et on revient en arrière si l'API échoue.
  private pendingComboReorderRevert: { from: number; to: number } | null = null;
  private preserveLocalComboOrderAfterMove = false;

  constructor(
    private panelService: EditMaterialComboPanelService,
    private translationLoader: FuseTranslationLoaderService,
    public translate: TranslateService,
    public materialsService: MaterialsService,
    private dialog: MatDialog,
    public router: Router,
    private logger: NGXLogger,
    private spinner: NgxSpinnerService,
    private breakpointObserver: BreakpointObserver,
    private tagsService: TagsService,
    private materialCategoryService: MaterialCategoryService,
  ) {
    super();
    this.translationLoader.loadTranslations(english, french);
    const data: EditMaterialComboPanelData | null = this.panelService.getData();
    if (data?.material) {
      this.material = data.material;
    }
    this.combo = Array.isArray(data?.materials) ? [...data!.materials!] : [];
    this.comboCopy = JSON.parse(JSON.stringify(this.combo));
    this.creationMode = data?.mode === 'create';
    this.orderContext = !!data?.orderContext;
  }

  ngOnInit(): void {
    this.breakpointObserver.observe(['(max-width: 600px)']).subscribe({
      next: (result) => {
        this.isSmallScreen = result.matches;
      },
    });

    this.materialCategoryService.getMaterialCategories().subscribe({
      next: (response: any) => {
        this.materialCategories = response?.data ?? [];
      },
      error: (error: any) => {
        this.logger.error(
          'EditMaterialComboPanelComponent.getMaterialCategories() -> ERROR: ' +
            JSON.stringify(error),
        );
      },
    });

    const tagFamilies = [
      TagsService.TAG_FAMILY_MATERIAL_TYPE,
      TagsService.TAG_FAMILY_MATERIAL_CUSTOM,
    ];
    for (const family of tagFamilies) {
      this.tagsService.getTagsByFamily(family).subscribe({
        next: (response: any) => {
          const tags = response.data || response || [];
          this.availableTags = [
            ...this.availableTags,
            ...tags.filter(
              (t: any) => !this.availableTags.some((at) => at.uuid === t.uuid),
            ),
          ];
        },
      });
    }
  }

  ngAfterViewInit(): void {
    this.fromEventSubscription = fromEvent(this.filter?.nativeElement, 'keyup')
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap(() => {
          if (this.canSearch) {
            this.loadMaterialList();
          } else {
            this.materials = [];
            this.isEmpty = true;
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.fromEventSubscription?.unsubscribe();
  }

  // ---------------------------------------------------------------- recherche

  get hasActiveFilters(): boolean {
    return !!this.selectedCategoryUuid || this.selectedTagUuids.length > 0;
  }

  get canSearch(): boolean {
    return (this.filterValue?.trim().length ?? 0) >= 2 || this.hasActiveFilters;
  }

  get showSearchMaterialHint(): boolean {
    return (
      this.isEmpty &&
      !this.isLoading &&
      !this.hasActiveFilters &&
      (this.filterValue?.trim().length ?? 0) < this.minCharsForEmptyMessage
    );
  }

  get showNoSearchResults(): boolean {
    return (
      this.isEmpty &&
      !this.isLoading &&
      (this.hasActiveFilters ||
        (this.filterValue?.trim().length ?? 0) >= this.minCharsForEmptyMessage)
    );
  }

  public onMaterialFilterChange(): void {
    if (this.materialList?.nativeElement) {
      this.materialList.nativeElement.scrollTop = 0;
    }
    if (this.canSearch) {
      this.loadMaterialList();
    } else {
      this.materials = [];
      this.isEmpty = true;
    }
  }

  public loadMaterialList(): void {
    this.isLoading = true;
    this.isEmpty = false;
    this.materialsService
      .getMaterialsWithoutCombo(
        this.filterValue,
        this.selectedCategoryUuid,
        this.selectedTagUuids,
      )
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          const list: any[] = (response?.data ?? []).filter(
            (m: any) => m.uuid !== this.material?.uuid,
          );
          this.materials = list;
          this.isEmpty = list.length === 0;
          if (this.materialList?.nativeElement) {
            this.materialList.nativeElement.scrollTop = 0;
          }
        },
        error: (error: any) => {
          this.logger.error(
            'EditMaterialComboPanelComponent.loadMaterialList() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.handleAPIError(error, this.dialog, null, null, this.router);
          this.isLoading = false;
          this.isEmpty = true;
        },
      });
  }

  public clearSearch(): void {
    this.filterValue = '';
    if (this.hasActiveFilters) {
      this.loadMaterialList();
    } else {
      this.materials = [];
      this.isEmpty = true;
    }
  }

  // ------------------------------------------------------------ drag & drop

  public onDrop(event: any): void {
    if (event.previousContainer === event.container) {
      if (event.previousIndex === event.currentIndex) {
        return;
      }
      moveItemInArray(this.combo, event.previousIndex, event.currentIndex);
      this.preserveLocalComboOrderAfterMove = true;
      this.pendingComboReorderRevert = {
        from: event.previousIndex,
        to: event.currentIndex,
      };
      this.onChildDrop(event.item.data, event.currentIndex);
      return;
    }

    if (event.item.data == undefined) {
      return;
    }
    this.attachToCombo(event.item.data, event.currentIndex);
  }

  /**
   * Attache un matériel catalogue à l'ensemble, puis le place à `targetIndex`
   * si un index de dépôt a été fourni. `combo` est remplacé par la réponse API
   * (indispensable en contexte commande : l'enfant est un clone, uuid neuf).
   */
  private attachToCombo(item: any, targetIndex?: number): void {
    this.beginChildProcess();
    const knownUuids = new Set(this.combo.map((c) => c.uuid));
    this.materialsService
      .productComboAttach(this.material.uuid, item.uuid, 1)
      .subscribe({
        next: (response: any) => {
          const childs: any[] = response?.data?.childs ?? [];
          this.combo = childs;
          this.comboCopy = JSON.parse(JSON.stringify(this.combo));
          const newChild =
            childs.find((c) => !knownUuids.has(c.uuid)) ??
            childs[childs.length - 1];
          const lastIndex = childs.length - 1;
          if (
            newChild &&
            targetIndex !== undefined &&
            targetIndex >= 0 &&
            targetIndex < lastIndex
          ) {
            moveItemInArray(this.combo, lastIndex, targetIndex);
            this.preserveLocalComboOrderAfterMove = true;
            this.pendingComboReorderRevert = {
              from: lastIndex,
              to: targetIndex,
            };
            this.onChildDrop(newChild, targetIndex);
          } else {
            this.endChildProcess();
          }
        },
        error: (error: any) => {
          this.logger.error(
            'EditMaterialComboPanelComponent.attachToCombo() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.endChildProcess();
          this.handleAPIError(error, this.dialog, null, null, this.router);
        },
      });
  }

  /** Persiste la position locale de `child` (index `movedToIndex`) via move-before / move-after. */
  private onChildDrop(child: any, movedToIndex?: number): void {
    if (this.combo.length < 2) {
      this.resetReorderState();
      this.endChildProcess();
      return;
    }

    let i = -1;
    if (
      movedToIndex !== undefined &&
      movedToIndex >= 0 &&
      movedToIndex < this.combo.length &&
      this.combo[movedToIndex]?.uuid === child?.uuid
    ) {
      i = movedToIndex;
    } else {
      i = this.combo.findIndex((c) => c.uuid === child?.uuid);
    }
    if (i < 0) {
      this.resetReorderState();
      this.endChildProcess();
      return;
    }

    this.beginChildProcess();
    const request$ =
      i === 0
        ? this.materialsService.productChildMoveBefore(
            this.material.uuid,
            child.uuid,
            this.combo[i + 1].uuid,
          )
        : this.materialsService.productChildMoveAfter(
            this.material.uuid,
            child.uuid,
            this.combo[i - 1].uuid,
          );

    request$.subscribe({
      next: (response: any) => this.applyMoveApiSuccess(response),
      error: (error: any) => {
        this.logger.error(
          'EditMaterialComboPanelComponent.onChildDrop() -> ERROR (move): ' +
            JSON.stringify(error),
        );
        this.applyMoveApiError(error);
      },
    });
  }

  private applyMoveApiSuccess(response: any): void {
    const dragReorderOnly = this.preserveLocalComboOrderAfterMove;
    this.pendingComboReorderRevert = null;
    this.endChildProcess();
    if (!dragReorderOnly) {
      const childs = response?.data?.childs;
      if (Array.isArray(childs) && childs.length === this.combo.length) {
        this.combo = childs;
      }
    }
    this.preserveLocalComboOrderAfterMove = false;
    // Après un pur réordonnancement, comboCopy n'est PAS réalignée : combosEqual()
    // reste faux et « Enregistrer » renvoie l'ordre complet (ceinture + bretelles).
    if (!dragReorderOnly) {
      this.comboCopy = JSON.parse(JSON.stringify(this.combo));
    }
  }

  private applyMoveApiError(error: any): void {
    if (this.pendingComboReorderRevert) {
      const { from, to } = this.pendingComboReorderRevert;
      moveItemInArray(this.combo, to, from);
    }
    this.resetReorderState();
    this.endChildProcess();
    this.handleAPIError(error, this.dialog, null, null, this.router);
  }

  private resetReorderState(): void {
    this.pendingComboReorderRevert = null;
    this.preserveLocalComboOrderAfterMove = false;
  }

  // ----------------------------------------------------------- liste droite

  /** Bouton + de la liste de gauche : ajoute (ou incrémente si déjà présent, hors commande). */
  public onAddFromCatalogue(item: any): void {
    const existing = this.orderContext
      ? undefined
      : this.combo.find((c) => c.uuid === item.uuid);
    if (existing) {
      this.onAddItem(existing);
      return;
    }
    this.attachToCombo(item);
  }

  public onAddItem(item: any): void {
    const previous = Number(item.qty) || 0;
    item.qty = previous + 1;
    this.persistChildQtyAndFlags(item, () => {
      item.qty = previous;
    });
  }

  public onLessItem(item: any): void {
    const previous = Number(item.qty) || 0;
    if (previous - 1 <= 0) {
      return;
    }
    item.qty = previous - 1;
    this.persistChildQtyAndFlags(item, () => {
      item.qty = previous;
    });
  }

  public onItemQtyChange(value: any, item: any): void {
    // Mutation en place : conserver l'identité de l'objet évite que @for
    // détruise/recrée la ligne (perte de focus). Persisté par « Enregistrer ».
    item.qty = value;
  }

  public onInternalViewChange(checked: boolean, item: any): void {
    const previous = item.internal_view_only;
    item.internal_view_only = checked;
    this.persistChildQtyAndFlags(item, () => {
      item.internal_view_only = previous;
    });
  }

  public onShowUnitPriceChange(checked: boolean, item: any): void {
    const previous = item.show_unit_price;
    item.show_unit_price = checked;
    this.persistChildQtyAndFlags(item, () => {
      item.show_unit_price = previous;
    });
  }

  /** Route à 5 segments (qty + les 2 flags) : la seule qui existe côté API pour un enfant. */
  private persistChildQtyAndFlags(item: any, revert: () => void): void {
    const qty = Number(item.qty);
    if (!qty || qty <= 0) {
      revert();
      return;
    }
    this.beginChildProcess();
    this.materialsService
      .productComboUpdateBoolean(
        this.material.uuid,
        item.uuid,
        qty,
        this.flagToInt(item.internal_view_only),
        this.flagToInt(item.show_unit_price, true),
      )
      .subscribe({
        next: () => {
          this.syncCopyFor(item);
          this.endChildProcess();
        },
        error: (error: any) => {
          this.logger.error(
            'EditMaterialComboPanelComponent.persistChildQtyAndFlags() -> ERROR: ' +
              JSON.stringify(error),
          );
          revert();
          this.endChildProcess();
          this.handleAPIError(error, this.dialog, null, null, this.router);
        },
      });
  }

  private flagToInt(value: any, defaultTrue: boolean = false): 0 | 1 {
    if (value === undefined || value === null) {
      return defaultTrue ? 1 : 0;
    }
    return value === false || value === 0 || value === '0' || value === 'false'
      ? 0
      : 1;
  }

  /** Aligne la copie de référence sur une ligne persistée (qty/flags), sans toucher à l'ordre. */
  private syncCopyFor(item: any): void {
    const copy = this.comboCopy.find((c) => c.uuid === item.uuid);
    if (copy) {
      copy.qty = item.qty;
      copy.internal_view_only = item.internal_view_only;
      copy.show_unit_price = item.show_unit_price;
    }
  }

  public onRemoveItem(item: any): void {
    const index = this.combo.indexOf(item);
    if (index < 0) {
      return;
    }
    this.combo.splice(index, 1);
    this.beginChildProcess();
    this.materialsService
      .productComboDetach(this.material.uuid, item.uuid, 0)
      .subscribe({
        next: () => {
          this.comboCopy = this.comboCopy.filter((c) => c.uuid !== item.uuid);
          this.endChildProcess();
        },
        error: (error: any) => {
          this.logger.error(
            'EditMaterialComboPanelComponent.onRemoveItem() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.combo.splice(index, 0, item);
          this.endChildProcess();
          this.handleAPIError(error, this.dialog, null, null, this.router);
        },
      });
  }

  // ------------------------------------------------------------ sauvegarde

  /** Ordre ET quantités : un pur réordonnancement compte comme un changement. */
  private combosEqual(): boolean {
    if (this.combo.length !== this.comboCopy.length) {
      return false;
    }
    for (let i = 0; i < this.combo.length; i++) {
      const a = this.combo[i];
      const b = this.comboCopy[i];
      if (a.uuid !== b.uuid || Number(a.qty) !== Number(b.qty)) {
        return false;
      }
    }
    return true;
  }

  public saveData(): void {
    if (this.saving || this.childProcess) {
      return;
    }
    if (this.combo.length === 0 || this.combosEqual()) {
      this.panelService.emitResult(true);
      return;
    }
    const children = this.combo.map((c, i) => ({
      uuid: c.uuid,
      qty: Number(c.qty) || 1,
      order: i,
    }));
    this.saving = true;
    this.materialsService
      .materialComboQtyUpdate(this.material.uuid, children)
      .subscribe({
        next: () => {
          this.saving = false;
          this.panelService.emitResult(true);
        },
        error: (error: any) => {
          this.saving = false;
          this.logger.error(
            'EditMaterialComboPanelComponent.saveData() -> ERROR: ' +
              JSON.stringify(error),
          );
          this.handleAPIError(error, this.dialog, null, null, this.router);
        },
      });
  }

  public close(): void {
    this.panelService.emitResult(false);
  }

  // ---------------------------------------------------------------- divers

  public onCreateMaterial(): void {
    this.newDialogRef = this.dialog.open(CreateMaterialComponent, {
      width: '340px',
      disableClose: false,
      data: { autoRedirect: false },
    });

    this.newDialogRef.afterClosed().subscribe({
      next: (result: any) => {
        if (result) {
          this.attachToCombo(result);
          if (this.canSearch) {
            this.loadMaterialList();
          }
        }
        this.newDialogRef = null;
      },
    });
  }

  public onOpenInNewTab(item: any): void {
    const prefix = localStorage.getItem('PREFIX') || '';
    window.open(prefix + ItemUrlFinder.getItemURL(item), '_blank');
  }

  private beginChildProcess(): void {
    this.childProcess = true;
    this.spinner.show(SPINNER);
  }

  private endChildProcess(): void {
    this.childProcess = false;
    this.spinner.hide(SPINNER);
  }
}
